import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { apiService, ApiError } from "../../services/chatApi";
import {
  ApiResponse,
  ChatMessage,
  ChatState,
  FeedbackData,
  Question,
  SavedMessage,
  Session,
} from "../../types/chat.types";

const initialState: ChatState = {
  sessions: {},
  currentSessionId: null,
  loading: false,
  error: null,
  messages: {},
  savedMessages: JSON.parse(localStorage.getItem("savedMessages") || "[]"),
};

export const startNewSession = createAsyncThunk<
  Session,
  Question,
  { rejectValue: string }
>("chat/startNewSession", async (question: Question, { rejectWithValue }) => {
  try {
    return await apiService.startSession(question);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue("Fehler beim Starten der Sitzung");
  }
});

export const askInSession = createAsyncThunk<
  { sessionId: string; question: string; response: ApiResponse },
  { sessionId: string; question: Question },
  { rejectValue: { message: string; isSessionError?: boolean } }
>("chat/askInSession", async ({ sessionId, question }, { rejectWithValue, dispatch }) => {
  try {
    const response = await apiService.askInSession(sessionId, question);
    return { sessionId, question: question.question, response };
  } catch (error) {
    if (error instanceof ApiError && error.isSessionError) {
      // Try to start a new session automatically
      try {
        const newSession = await apiService.startSession(question);
        return { 
          sessionId: newSession.session_id, 
          question: question.question, 
          response: { content: newSession.content, references: newSession.references }
        };
      } catch (retryError) {
        return rejectWithValue({ 
          message: error.message, 
          isSessionError: true
        });
      }
    }
    if (error instanceof ApiError) {
      return rejectWithValue({ message: error.message });
    }
    return rejectWithValue({ message: "Fehler beim Stellen der Frage in der Sitzung" });
  }
});

export const deleteSession = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("chat/deleteSession", async (sessionId: string, { rejectWithValue }) => {
  try {
    await apiService.deleteSession(sessionId);
    return sessionId;
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue("Fehler beim Löschen der Sitzung");
  }
});

export const submitFeedback = createAsyncThunk<
  { sessionId: string; messageId: string; rating: 'up' | 'down' },
  FeedbackData & { messageId: string },
  { rejectValue: string }
>("chat/submitFeedback", async (feedbackData, { rejectWithValue }) => {
  try {
    await apiService.submitFeedback({
      session_id: feedbackData.session_id,
      rating: feedbackData.rating,
      comment: feedbackData.comment
    });
    return {
      sessionId: feedbackData.session_id,
      messageId: feedbackData.messageId,
      rating: feedbackData.rating
    };
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue("Fehler beim Senden des Feedbacks");
  }
});

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentSession: (state, action: PayloadAction<string | null>) => {
      state.currentSessionId = action.payload;
    },
    rateMessage: (
      state,
      action: PayloadAction<{
        sessionId: string;
        messageId: string;
        rating: "up" | "down";
      }>
    ) => {
      const { sessionId, messageId, rating } = action.payload;
      const message = state.messages[sessionId]?.find(
        (m) => m.id === messageId
      );
      if (message) {
        message.rating = rating;
      }
    },
    saveMessage: (
      state,
      action: PayloadAction<{ sessionId: string; messageId: string }>
    ) => {
      const { sessionId, messageId } = action.payload;
      const message = state.messages[sessionId]?.find(
        (m) => m.id === messageId
      );
      if (message && !message.saved) {
        message.saved = true;
        const savedMessage: SavedMessage = {
          id: messageId,
          content: message.answer.content,
          timestamp: message.timestamp,
          sessionId,
          references: message.answer.references || [], // Include references
        };
        state.savedMessages.push(savedMessage);
        localStorage.setItem(
          "savedMessages",
          JSON.stringify(state.savedMessages)
        );
      }
    },
    removeSavedMessage: (state, action: PayloadAction<string>) => {
      // Find the saved message to get the sessionId
      const savedMessage = state.savedMessages.find(m => m.id === action.payload);
      
      // Remove from saved messages
      state.savedMessages = state.savedMessages.filter(
        (m) => m.id !== action.payload
      );
      
      // Update the original message's saved status
      if (savedMessage && savedMessage.sessionId) {
        const originalMessage = state.messages[savedMessage.sessionId]?.find(
          (m) => m.id === savedMessage.id
        );
        if (originalMessage) {
          originalMessage.saved = false;
        }
      }
      
      localStorage.setItem(
        "savedMessages",
        JSON.stringify(state.savedMessages)
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startNewSession.pending, (state, action) => {
        state.loading = true;
        state.error = null;

        const userMessage: ChatMessage = {
          id: Date.now().toString(),
          question: action.meta.arg.question,
          answer: { content: "" },
          timestamp: new Date().toISOString(),
        };

        const tempSessionId = `temp-${Date.now()}`;
        state.currentSessionId = tempSessionId;
        state.messages[tempSessionId] = [userMessage];
      })
      .addCase(startNewSession.fulfilled, (state, action) => {
        state.loading = false;
        const session = action.payload;
        const tempSessionId = state.currentSessionId;

        // Move messages from temp session to real session
        if (tempSessionId && state.messages[tempSessionId]) {
          const tempMessages = state.messages[tempSessionId];
          delete state.messages[tempSessionId];

          // Update the user message with AI response
          tempMessages[0].answer = {
            content: session.content,
            references: session.references,
          };

          state.messages[session.session_id] = tempMessages;
        }

        state.sessions[session.session_id] = session;
        state.currentSessionId = session.session_id;
      })
      .addCase(startNewSession.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Fehler beim Starten der Sitzung";

        // Remove temp session on error
        if (
          state.currentSessionId &&
          state.currentSessionId.startsWith("temp-")
        ) {
          delete state.messages[state.currentSessionId];
          state.currentSessionId = null;
        }
      })
      // Ask in Session
      .addCase(askInSession.pending, (state, action) => {
        state.loading = true;
        state.error = null;

        // Add user message immediately for better UX
        const userMessage: ChatMessage = {
          id: Date.now().toString(),
          question: action.meta.arg.question.question,
          answer: { content: "" },
          timestamp: new Date().toISOString(),
        };

        const sessionId = action.meta.arg.sessionId;
        if (!state.messages[sessionId]) {
          state.messages[sessionId] = [];
        }
        state.messages[sessionId].push(userMessage);
      })
      .addCase(askInSession.fulfilled, (state, action) => {
        state.loading = false;
        const { sessionId, response } = action.payload;

        // If sessionId changed (due to session recovery), update current session
        if (sessionId !== state.currentSessionId) {
          state.currentSessionId = sessionId;
          // Create new session record if it doesn't exist
          if (!state.sessions[sessionId]) {
            state.sessions[sessionId] = {
              session_id: sessionId,
              content: response.content,
              references: response.references
            };
          }
        }

        // Update the last message with AI response
        const messages = state.messages[sessionId];
        if (messages && messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          lastMessage.answer = response;
        }
      })
      .addCase(askInSession.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload;
        
        if (payload && typeof payload === 'object' && 'message' in payload) {
          state.error = payload.message;
          
          // If it's a session error, clear current session
          if (payload.isSessionError) {
            state.currentSessionId = null;
          }
        } else {
          state.error = "Fehler beim Stellen der Frage in der Sitzung";
        }

        // Remove the last user message on error
        const sessionId = Object.keys(state.messages).find(
          (id) => state.messages[id].length > 0
        );
        if (sessionId) {
          const messages = state.messages[sessionId];
          const lastMessage = messages[messages.length - 1];
          if (lastMessage && !lastMessage.answer.content) {
            messages.pop();
          }
        }
      })
      // Delete Session
      .addCase(deleteSession.fulfilled, (state, action) => {
        const sessionId = action.payload;
        delete state.sessions[sessionId];
        delete state.messages[sessionId];
        if (state.currentSessionId === sessionId) {
          state.currentSessionId = null;
        }
      })
      .addCase(deleteSession.rejected, (state, action) => {
        state.error = action.payload || "Fehler beim Löschen der Sitzung";
      })
      // Submit Feedback
      .addCase(submitFeedback.fulfilled, (state, action) => {
        const { sessionId, messageId, rating } = action.payload;
        const message = state.messages[sessionId]?.find(
          (m) => m.id === messageId
        );
        if (message) {
          message.rating = rating;
        }
      })
      .addCase(submitFeedback.rejected, (state, action) => {
        state.error = action.payload || "Fehler beim Senden des Feedbacks";
      });
  },
});

export const {
  clearError,
  setCurrentSession,
  rateMessage,
  saveMessage,
  removeSavedMessage,
} = chatSlice.actions;

export default chatSlice.reducer;
